import { useEffect, useMemo, useState } from 'react';

import {
  ListNpsScoresDocument,
  ListNpsScoresQuery,
  ListNpsScoresQueryVariables,
  NpsScoreFieldsFragment,
  NpsScoreWhereInput,
  OrderByArgs,
  PaginationArgs,
} from '@/@generated/codegen/API';
import { useApolloClientContext } from '@/components/CustomApolloProvider/CustomApolloProvider';
import { useEntitySubscription } from '@/hooks/useEntitySubscription';
import { captureSentry } from '@/utils/helpers/sentryHelper';

export default function useMeetingNPSScores(meetingID: string | undefined, live: boolean) {
  const { apolloClient } = useApolloClientContext();
  const [loading, setLoading] = useState<boolean>(false);
  const [scoresPerUser, setScoresPerUser] = useState<Map<string, NpsScoreFieldsFragment[]>>(
    new Map()
  );
  const [npsScores, setNpsScores] = useState<number[]>([]);
  const npsAverage = useMemo(() => {
    if (npsScores.length === 0) {
      return 1;
    }
    return npsScores.reduce((prev, curr) => prev + curr, 0) / npsScores.length / 10; //Note: divide by 10 which is max score
  }, [npsScores]);

  const where: NpsScoreWhereInput = useMemo(() => {
    return {
      meetingId: {
        equals: meetingID,
      },
    };
  }, [meetingID]);

  // NIKO TODO: use useEntityFetchAll instead of this
  const fetchAllNPSScores = async () => {
    if (!meetingID) return;
    setLoading(true);

    let hasMore = true;
    let cursor: string | undefined = undefined;
    let allNpsScores: NpsScoreFieldsFragment[] = [];

    while (hasMore) {
      const query = await apolloClient.query<ListNpsScoresQuery, ListNpsScoresQueryVariables>({
        query: ListNpsScoresDocument,
        variables: {
          orderByArgs: {
            orderByField: 'createdAt',
            direction: 'desc',
          } satisfies OrderByArgs,
          paginationArgs: {
            first: 100,
            after: cursor,
          } satisfies PaginationArgs,
          where,
        },
        fetchPolicy: 'network-only',
      });

      const data: ListNpsScoresQuery = query.data;

      const elems = data.listNPSScores.edges.map((el) => el.node);
      allNpsScores = [...allNpsScores, ...elems];

      const pageInfo = data.listNPSScores.pageInfo;
      cursor = pageInfo.endCursor ?? undefined;
      hasMore = pageInfo.hasNextPage;
    }

    const scoresList =
      allNpsScores.map((item) => {
        if (!item?.scores) return [];
        return item.scores;
      }) || [];
    const scorePerUsers =
      allNpsScores.reduce((acc, curr) => {
        if (!curr?.scores) return acc;
        acc.set(curr.userId, [...(acc.get(curr.userId) || []), curr]);
        return acc;
      }, new Map<string, NpsScoreFieldsFragment[]>()) || new Map();
    const scores = scoresList.reduce((prev, curr) => [...prev, ...curr], []);
    setNpsScores(scores);
    setScoresPerUser(scorePerUsers);
    setLoading(false);
  };

  useEffect(() => {
    void fetchAllNPSScores();
  }, [where]);

  if (live) {
    useEntitySubscription('NPSScore', {
      onCreate: {
        variables: {
          where,
        },
        onData: (res) => {
          const obj = res.data.data?.onCreateNPSScore;
          if (!obj) {
            captureSentry({
              title: 'useMeetingNPSScores | onData | obj is undefined',
              detail: {},
            });
            return;
          }
          setNpsScores((prev) => [...prev, ...(obj.scores || [])]);
          setScoresPerUser((prev) => {
            prev.set(obj.userId, [...(prev.get(obj.userId) || []), obj]);
            return prev;
          });
        },
      },
    });
  }

  return {
    scoresPerUser,
    npsScores,
    npsAverage,
    isLoading: loading,
  };
}
